import classNames from 'classnames';
import * as React from 'react';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import { SubjectData } from '~/Pages/SubjectInfoPage/Components/SubjectData/SubjectData';
import { SubjectInfoDescription } from '~/Pages/SubjectInfoPage/Components/SubjectInfoDescription/SubjectInfoDescription';
import type { SubjectContentTranslations } from '~/Pages/SubjectInfoPage/SubjectContentTranslations.csharp';
import type { SubjectInfoDescriptionViewModel } from '~/Pages/SubjectInfoPage/SubjectInfoDescriptionViewModel.csharp';
import type { SemesterDropdownItem } from '~/Pages/SubjectInfoPage/ViewModel/SemesterDropdownItem.csharp';
import baseStyles from '~/Pages/SyllabusListPage/SyllabusInfoPageBase.module.css';
import { Button } from '~/Partials/Button/Button';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { Dropdown } from '~/Partials/Dropdown/Dropdown';
import { Link } from '~/Partials/Link/Link';
import { LinkType } from '~/Partials/Link/LinkType.csharp';
import styles from './Content.module.css';

export interface ContentProps {
    translations: SubjectContentTranslations;
    semesterDropdownItems: SemesterDropdownItem[];
    onlineSubjectPageUrl: string;
    studyLevel: string;
    code: string;
    points: number;
    version: string;
    englishName: string;
    setCurrentSemester: (newSemester: SemesterDropdownItem) => void;
    setQueryParams: (semester: SemesterDropdownItem) => void;
    subjectInfoDescription?: SubjectInfoDescriptionViewModel;
    placeholder?: HtmlString;
    currentSemester?: SemesterDropdownItem;
}

export const Content: React.FC<ContentProps> = props => {
    const {
        studyLevel,
        setCurrentSemester,
        currentSemester,
        placeholder,
        semesterDropdownItems,
        englishName,
        points,
        subjectInfoDescription,
        code,
        version,
        onlineSubjectPageUrl,
        setQueryParams,
        translations,
    } = props;

    const onDropdownValueChanged = (value: string) => {
        const newSemester = JSON.parse(value);
        setCurrentSemester(newSemester);
        setQueryParams(newSemester);
    };

    const dropdownOptions = semesterDropdownItems.map(item => ({
        text: item.displayName,
        value: JSON.stringify(item),
    }));

    const renderHeading = (heading: string, subheading: string | number) => (
        <li>
            <h3>{heading}</h3>
            <span>{subheading}</span>
        </li>
    );

    return (
        <>
            <div className={baseStyles.InfoTableContainer}>
                <div className={classNames(styles.InfoTable, baseStyles.InfoTable)}>
                    {!!currentSemester && (
                        <>
                            <p className={baseStyles['CurrentSemester--print']}>{currentSemester.displayName}</p>
                            <Dropdown
                                className={styles.Select}
                                label={translations.teachingPeriod}
                                options={dropdownOptions}
                                onValueChanged={onDropdownValueChanged}
                                selectedValue={JSON.stringify(currentSemester)}
                            />
                        </>
                    )}
                    {!!currentSemester &&
                        (!!currentSemester.pdfPath ? (
                            <Link
                                className={styles.Pdf}
                                href={currentSemester.pdfPath}
                                label={translations.downloadPdf}
                                type={LinkType.Button}
                                buttonType={ButtonType.Outline}
                                download
                            />
                        ) : (
                            <Button
                                onClick={() => window.print()}
                                className={styles.Pdf}
                                tier={ButtonTier.LegacyPrimary}
                                type={ButtonType.Outline}
                                size={ButtonSize.Large}
                            >
                                {translations.print}
                            </Button>
                        ))}
                    <ul>
                        {renderHeading(translations.subjectCode, code)}
                        {renderHeading(translations.version, version)}
                        {!!englishName && (
                            <li>
                                <h3>{translations.englishName}</h3>
                                <span lang="en">{englishName}</span>
                            </li>
                        )}
                        {renderHeading(translations.studyPoints, points)}

                        {!!translations.studyLevel && renderHeading(translations.studyLevel, studyLevel)}
                        {!!subjectInfoDescription && (
                            <SubjectData subjectInfoDescription={subjectInfoDescription} translations={translations} />
                        )}
                    </ul>
                    {!!onlineSubjectPageUrl && (
                        <Link
                            className={styles.StudyRedirect}
                            type={LinkType.Button}
                            href={onlineSubjectPageUrl}
                            label={translations.goToStudyOffers}
                        />
                    )}
                </div>
            </div>
            <div className={classNames('RichtextContent', styles.Description, baseStyles.Description)}>
                <SubjectInfoDescription
                    manualContent={subjectInfoDescription?.manualContent}
                    description={subjectInfoDescription?.description}
                    placeholder={placeholder}
                />
            </div>
        </>
    );
};
